import React, {useEffect, useState} from 'react';
import styles from './AdminProjectRequestInfo.module.css';
import {GetAdminProjectRequestResponseData} from "../../../types/admin";
import TextCard from "../../common/textCard/TextCard";
import {adminProjectRequestAPI, projectRequestSampleAPI} from "../../../services/admin";
import ConfirmProjectRequestPopUp from "../../../views/admin/popup/ConfirmProjectRequestPopUp";
import logo from '../../../assets/covi_logo_white.png';
import Button from "../../common/button/Button";

interface AdminProjectRequestInfoProps {
    requestId: number;
}

//프로젝트 추가 요청 정보
const AdminProjectRequestInfo: React.FC<AdminProjectRequestInfoProps> = ({requestId}) => {
    const [data, setData] = React.useState<GetAdminProjectRequestResponseData | null>(null);
    const [sample, setSample] = React.useState<string>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = React.useState(true);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        fetchData();
    }

    const handleOpenModal = () => {
        setIsModalOpen(true);
    }

    const fetchData = async () => {
        try {
            setLoading(true);
            //어드민 프로젝트 추가 요청 조회 API
            const response = await adminProjectRequestAPI(requestId);
            setData(response);
            // 어드민 프로젝트 추가 요청 샘플 데이터 조회 API
            const file = await projectRequestSampleAPI(requestId);
            // 파일 내부 URL 생성
            const url = URL.createObjectURL(file);
            setSample(url);
        } catch (error) {
            console.error('Error fetching project request:', error);
        } finally {
            setLoading(false);
        }
    };

    //요일
    const formatDate = (date: Date | undefined, day: number | undefined) => {
        if (!date || day === undefined) return '-';
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    useEffect(() => {
        fetchData();
    }, [requestId]);

    //날짜 정보
    const dateInfo = [
        {
            label: "최초 요청일",
            value: formatDate(data?.createdAt, data?.createDay)
        },
        {
            label: "마지막 수정일",
            value: formatDate(data?.updatedAt, data?.updateDay)
        },
        {
            label: "희망 시작일",
            value: formatDate(data?.startAt, data?.startDay)
        },
        {
            label: "희망 종료일",
            value: formatDate(data?.deadline, data?.deadlineDay)
        }
    ];

    //프로젝트 정보
    const projectInfo = [
        {
            label: "처리 상태",
            value: data?.status?.toString() || '-'
        },
        {
            label: "처리일",
            value: formatDate(data?.confirmedAt, data?.confirmDay)
        },
        {
            label: "데이터 수량",
            value: data?.dataAmount ? `${data.dataAmount.toLocaleString()}장` : '-'
        },
        {
            label: "가공 방식",
            value: data?.projectType?.toString() || '-'
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    //프로젝트 추가 요청 상태가 대기인경우
    const showProcessButton = data?.status === '대기';

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleContainer}>
                    <h1 className={styles.title}>Project Request Information</h1>
                    <p className={styles.subtitle}>추가 요청 프로젝트 상태 정보</p>
                </div>
                {showProcessButton && (
                    // 프로젝트 추가 요청 상태가 대기인 경우 처리 버튼 출력
                    <Button
                        onClick={handleOpenModal}
                        variant={"primary"}
                    >
                        처리
                    </Button>
                )}
            </div>

            <div className={styles.mainContent}>
                <div className={styles.leftSection}>
                    <div className={styles.imageContainer}>
                        {/*샘플 데이터*/}
                        {sample ? (
                            <img
                                src={sample}
                                alt="Project Preview"
                                className={styles.projectImage}
                                onError={(e) => {
                                    e.currentTarget.src = logo;
                                }}
                            />
                        ) : (
                            <div className={styles.noImage}>No image available</div>
                        )}
                    </div>

                    <div className={styles.infoGrid}>
                        {/*프로젝트 추가 요청 정보*/}
                        <TextCard items={dateInfo}/>
                        <TextCard items={projectInfo}/>
                    </div>
                </div>

                <div className={styles.leftNote}>
                    {/*프로젝트명*/}
                    <h3 className={styles.projectName}>{data?.projectName || '-'}</h3>
                    {/*프로젝트 추가 요청 특이사항 설명*/}
                    <div className={styles.noteContent}>
                        {data?.explanation}
                    </div>
                </div>
            </div>
            {/*프로젝트 추가 요청 처리 팝업*/}
            <ConfirmProjectRequestPopUp
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                requestId={data?.projectRequestId}
            />
        </div>
    );
};

export default AdminProjectRequestInfo;